import { renderDsButton } from '../../../../../shared-ui/ds/button.js';
import { renderFinalUiOutlinedIcon } from '../../../../../apps/tutor-desk/plugins/_shared/final_ui_action_registry.js';
import {
  isSettingsSaasFinalUiActive,
  resolveSettingsFinalUiAction,
  withSettingsPresentationMode
} from './settingsPresentationMode.js';

const BUTTON_INTENTS = new Set(['Primary', 'Secondary', 'Danger']);
const BUTTON_STATES = new Set(['Default', 'Hover', 'Active', 'Focus', 'Disabled', 'Loading']);
const BUTTON_SIZES = new Set(['sm', 'md', 'lg']);
const BUTTON_STATE_CLASSES = Object.freeze([
  'ds-button--state-default',
  'ds-button--state-hover',
  'ds-button--state-active',
  'ds-button--state-focus',
  'ds-button--state-disabled',
  'ds-button--state-loading'
]);
const SETTINGS_BUTTON_RUNTIME_SNAPSHOTS = new WeakMap();

export function renderSettingsButton(options = {}) {
  const doc = options && typeof options === 'object' ? options.doc : undefined;
  const normalized = withSettingsPresentationMode(options, doc);
  const attrs = normalized && typeof normalized.attrs === 'object' && normalized.attrs
    ? { ...normalized.attrs }
    : normalized.attrs;
  const intent = BUTTON_INTENTS.has(normalized.intent)
    ? normalized.intent
    : (normalized.danger ? 'Danger' : normalized.secondary ? 'Secondary' : 'Primary');
  const state = BUTTON_STATES.has(normalized.state)
    ? normalized.state
    : (normalized.active ? 'Active' : 'Default');
  const size = BUTTON_SIZES.has(normalized.size)
    ? normalized.size
    : (normalized.compact ? 'sm' : 'md');
  const wrapperStyle = String(normalized.wrapperStyle || '').trim();
  const finalAction = resolveSettingsFinalUiAction(normalized, doc);
  const finalRole = String(finalAction?.role || '').trim();
  const finalSize = String(finalAction?.size || '').trim();
  const readyLabel = String(
    finalAction?.labelPolicy === 'supplied' && String(normalized.label || '').trim()
      ? normalized.label
      : finalAction?.canonicalLabel || normalized.label || ''
  ).trim();
  const finalLabel = applySettingsActionLabelCase(
    finalAction?.state === 'busy' && finalAction?.busyLabel
      ? finalAction.busyLabel
      : readyLabel,
    finalAction?.labelCase
  );
  const finalClassName = finalAction
    ? [
        String(normalized.className || '').trim(),
        'ttoria-final-ui-action',
        `ttoria-final-ui-action--${finalRole || 'secondary'}`,
        `ttoria-final-ui-action--${finalSize || 'secondary'}`
      ].filter(Boolean).join(' ')
    : normalized.className;
  const finalIconHtml = finalAction
    ? renderFinalUiOutlinedIcon(String(finalAction.icon || 'check'))
    : '';
  const buttonHtml = renderDsButton({
    ...normalized,
    attrs,
    label: finalAction ? finalLabel : normalized.label,
    intent: finalAction ? finalAction.intent : intent,
    state: finalAction?.state === 'busy' ? 'Loading' : state,
    size: finalAction && ['compact', 'table', 'icon', 'menu'].includes(finalRole) ? 'sm' : size,
    className: finalClassName,
    icon: finalAction ? 'left' : normalized.icon,
    iconLeftHtml: finalAction ? finalIconHtml : normalized.iconLeftHtml
  });

  if (!wrapperStyle || isSettingsSaasFinalUiActive(doc)) return buttonHtml;
  return `<span style="${escapeAttribute(wrapperStyle)}">${buttonHtml}</span>`;
}

export function setSettingsButtonRuntimeState(button, {
  busy = false,
  disabled = Boolean(button?.disabled),
  legacyLabel = '',
  doc = button?.ownerDocument
} = {}) {
  if (!button || typeof button !== 'object') return button;
  if (!isSettingsSaasFinalUiActive(doc)) {
    button.disabled = Boolean(disabled);
    if (legacyLabel) button.textContent = String(legacyLabel);
    return button;
  }

  const labelNode = button.querySelector?.('.ds-button__label') || null;
  const iconNode = button.querySelector?.('.ds-button__icon--left') || null;
  let snapshot = SETTINGS_BUTTON_RUNTIME_SNAPSHOTS.get(button);
  if (!snapshot) {
    snapshot = {
      label: String(labelNode?.textContent || ''),
      iconHtml: String(iconNode?.innerHTML || '')
    };
    SETTINGS_BUTTON_RUNTIME_SNAPSHOTS.set(button, snapshot);
  }

  const actionAttrs = readSettingsButtonActionAttributes(button);
  const finalAction = resolveSettingsFinalUiAction({
    label: snapshot.label,
    attrs: actionAttrs,
    busy
  }, doc);
  const uiState = busy ? 'busy' : disabled ? 'disabled' : 'default';
  const dsState = busy ? 'loading' : disabled ? 'disabled' : 'default';

  button.disabled = Boolean(disabled);
  button.setAttribute?.('data-kg-ui-state', uiState);
  if (busy) {
    button.setAttribute?.('aria-busy', 'true');
    button.setAttribute?.('data-tutor-desk-action-busy', 'true');
  } else {
    button.removeAttribute?.('aria-busy');
    button.removeAttribute?.('data-tutor-desk-action-busy');
  }
  if (button.classList) {
    button.classList.remove(...BUTTON_STATE_CLASSES);
    button.classList.add(`ds-button--state-${dsState}`);
  }

  if (busy) {
    if (labelNode) {
      labelNode.textContent = applySettingsActionLabelCase(
        finalAction?.busyLabel || snapshot.label,
        finalAction?.labelCase
      );
    }
    if (iconNode) {
      iconNode.innerHTML = renderFinalUiOutlinedIcon(String(finalAction?.icon || 'loader'));
    }
  } else {
    if (labelNode) labelNode.textContent = snapshot.label;
    if (iconNode) iconNode.innerHTML = snapshot.iconHtml;
    SETTINGS_BUTTON_RUNTIME_SNAPSHOTS.delete(button);
  }
  return button;
}

function readSettingsButtonActionAttributes(button) {
  const attrs = {};
  for (const name of ['data-action', 'data-kg-semantic-action-id', 'data-testid', 'role']) {
    const value = String(button?.getAttribute?.(name) || '').trim();
    if (value) attrs[name] = value;
  }
  return attrs;
}

function applySettingsActionLabelCase(value = '', labelCase = '') {
  const label = String(value || '').trim();
  if (labelCase !== 'title-case') return label;
  return label.replace(/(^|[\s/\-])([a-z])/gu, (_match, prefix, letter) => `${prefix}${letter.toUpperCase()}`);
}

function escapeAttribute(value = '') {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}
